import { Contact } from '~/src/entities/contact';
import {
  ProjectStackFieldGroup,
  ProjectStackField,
  ProjectStackDocument,
  Project,
  DocumentType,
} from '~/src/entities/project';
import { FIELD_ORDER, GROUPING_NOUNS } from '../constants';

/**
 * Checks if the project contains ONLY PDF court forms (all documents are TAGGED_PDF).
 * Custom sorting is only applied when all documents in the project are court forms.
 */
const isCourtFormProject = (project: Project): boolean => {
  const documents = project.stack_data?.documents ?? [];
  // Return false if no documents exist
  if (documents.length === 0) return false;

  return documents.every(
    (doc) => doc.document_type === DocumentType.TAGGED_PDF,
  );
};

/**
 * Temporary Sorting Logic (DRINT-783)
 * These functions provide custom field ordering until the tag architecture can handle this natively.
 * Extracts the grouping noun from a field label.
 */
const extractGroupingNoun = (label: string): string => {
  const trimmed = label.trim();

  // Pattern 1: Starts with noun (e.g., "Employer Name", "Mailing Address")
  for (const noun of GROUPING_NOUNS) {
    if (trimmed.startsWith(noun + ' ')) {
      return noun;
    }
  }

  // Pattern 2: Contains noun with word boundaries - e.g., "Start Date of Employment"
  for (const noun of GROUPING_NOUNS) {
    const regex = new RegExp(`\\b${noun}\\b`);
    if (regex.test(trimmed)) {
      return noun;
    }
  }

  return '';
};

/**
 * Gets the base field name after removing the grouping noun.
 */
const getBaseFieldName = (label: string, groupingNoun: string): string => {
  const trimmed = label.trim();

  // Pattern 1: Remove prefix noun (e.g., "Employer Name" → "Name")
  if (groupingNoun && trimmed.startsWith(groupingNoun + ' ')) {
    return trimmed.substring(groupingNoun.length).trim();
  }

  // Pattern 2: Remove embedded noun (e.g., "Start Date of Employment" → "Start Date")
  if (groupingNoun) {
    const regex = new RegExp(`\\s+of\\s+${groupingNoun}\\b`, 'i');
    const withoutNoun = trimmed.replace(regex, '');
    if (withoutNoun !== trimmed) {
      return withoutNoun.trim();
    }
  }

  return trimmed;
};

/**
 * Finds the index of a field in the predefined FIELD_ORDER using exact matching.
 */
const getFieldOrderIndex = (baseField: string): number => {
  const normalizedBase = baseField.toLowerCase().trim();

  const index = FIELD_ORDER.findIndex(
    (orderField) => normalizedBase === orderField.toLowerCase(),
  );

  return index === -1 ? FIELD_ORDER.length : index;
};

export const getDocumentsForField = (
  projectStackDocuments: ProjectStackDocument[],
  field: ProjectStackField,
) =>
  projectStackDocuments.filter((doc) => {
    if (field.fid in doc.document_fields) {
      return true;
    }
    return false;
  });

export const getFieldsForGroup = (
  project: Project,
  group: ProjectStackFieldGroup,
) => {
  const groupFields = group.fields ?? [];
  const stackFields = project.stack_data?.fields ?? [];

  const fields = groupFields
    .map((fieldId) => {
      const field = stackFields.find(({ fid }) => fid === fieldId);
      if (!field)
        console.error(
          `Missing field ${fieldId} in group ${group.label} in project ${project.id}`,
        );
      return field;
    })
    .filter((f): f is ProjectStackField => !!f);

  // Only apply custom sorting if ALL documents are PDF court forms
  if (!isCourtFormProject(project)) {
    return fields.sort((a, b) => {
      const sortOrderA =
        'sort_order' in a && a.sort_order !== undefined ? a.sort_order : 0;
      const sortOrderB =
        'sort_order' in b && b.sort_order !== undefined ? b.sort_order : 0;
      return sortOrderA - sortOrderB;
    });
  }

  // Custom sorting for projects with ONLY PDF court forms
  return fields.sort((a, b) => {
    const labelA = a.label || '';
    const labelB = b.label || '';

    const groupA = extractGroupingNoun(labelA);
    const groupB = extractGroupingNoun(labelB);
    const baseA = getBaseFieldName(labelA, groupA);
    const baseB = getBaseFieldName(labelB, groupB);

    // Primary sort: Group by noun (Employer, Employment, Mailing, etc.)
    if (groupA !== groupB) {
      if (groupA === '') return -1;
      if (groupB === '') return 1;
      return groupA.localeCompare(groupB);
    }

    // Secondary sort: By field type order (Name, Address, Date, etc.)
    // Skip FIELD_ORDER lookup for empty labels - let them fall through to sort_order
    const hasContentA = baseA.trim() !== '';
    const hasContentB = baseB.trim() !== '';

    if (hasContentA && hasContentB) {
      const orderA = getFieldOrderIndex(baseA);
      const orderB = getFieldOrderIndex(baseB);
      if (orderA !== orderB) return orderA - orderB;

      // Tertiary sort: Prefer shorter/simpler labels (this places things like Country before Country of Residence etc.)
      if (baseA.length !== baseB.length) return baseA.length - baseB.length;
    }
    // If one or both are empty, skip FIELD_ORDER and length checks, fall through to sort_order

    // Final sort: Fallback to backend order
    const sortOrderA =
      'sort_order' in a && a.sort_order !== undefined ? a.sort_order : 0;
    const sortOrderB =
      'sort_order' in b && b.sort_order !== undefined ? b.sort_order : 0;
    return sortOrderA - sortOrderB;
  });
};

export const getFieldLabelsForGroup = (
  project: Project,
  group: ProjectStackFieldGroup,
) => {
  const groupFields = group.fields ?? [];
  const stackFields = project.stack_data?.fields ?? [];

  return groupFields.map((fieldId) => {
    const field = stackFields.find(({ fid }) => fid === fieldId);
    if (!field) {
      console.error(
        `Missing field ${fieldId} in group ${group.label} in project ${project.id}`,
      );
    } else {
      return { fid: field.fid, full_label: `${group.label}, ${field.label}` };
    }
  });
};

export const getEmptyFields = (fields: ProjectStackField[]) => {
  const emptyFields = fields.filter((field) => {
    return !field.value;
  });
  return emptyFields;
};

export const getUpdatedFormFieldsForContact = (
  contact: Contact,
  keyMap: Record<string, string>,
  attributes: string[],
): string[] => {
  // Copy the contact fields that are not an object or a dictionary.
  const resolveFrom: Record<string, any> = { ...contact.data };
  for (const key in contact) {
    if (typeof contact[key as keyof Contact] !== 'object') {
      resolveFrom[key] = contact[key as keyof Contact];
    }
  }
  return attributes.map((attr) => {
    const resolvedAttr = keyMap[attr]!;
    // if the keyMap doesn't contain the attr (resolvedAttr), try and pull it from the
    // contacts data map by just using the attribute
    const resolvedValue = resolveFrom[resolvedAttr || attr];
    return resolvedValue ? `${resolvedValue}` : '';
  });
};
